/**
 * Screen 2 — AWAITING_TELEGRAM. Shown when the project is enrolled but has
 * zero linked chats. Two ways to link:
 *   1. Direct: click "Link Telegram Bot" — deep-links straight into t.me
 *   2. Invite: generate a link the user pastes to a teammate
 *
 * The invite-link endpoint returns URLs with a `{TELEGRAM_BOT_USERNAME}`
 * placeholder that we substitute client-side from `/v1/config`.
 */
import { useEffect, useState } from 'react';
import { Copy, MessageCircle, Send } from 'lucide-react';
import { toast } from 'sonner';
import type { Project } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Section } from '@/components/ui/section';
import { Spinner } from '@/components/spinner';
import { ApiError, summarizeApiError } from '@/lib/api';
import {
  useAutoCampaignInviteLink,
  useDisableAutoCampaign,
} from '@/features/auto-campaign/mutations';
import { useRuntimeConfig } from '@/features/auto-campaign/use-config';

/**
 * Replace the API's `{TELEGRAM_BOT_USERNAME}` placeholder with the actual bot
 * username (from `/v1/config`). Falls back to a warning string when the
 * username isn't set — the deployment is misconfigured and no link will work.
 */
function fillBotUsername(url: string, botUsername: string | null): string {
  if (!botUsername) return url; // caller renders the "not configured" copy
  return url.replaceAll('{TELEGRAM_BOT_USERNAME}', botUsername);
}

export interface TelegramLinkCardProps {
  project: Project;
}

export function TelegramLinkCard({ project }: TelegramLinkCardProps): JSX.Element {
  const config = useRuntimeConfig();
  const invite = useAutoCampaignInviteLink(project.id);
  const disable = useDisableAutoCampaign(project.id);
  const [linkNotConfigured, setLinkNotConfigured] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteExpiresAt, setInviteExpiresAt] = useState<string | null>(null);

  // Auto-generate the primary "Link Telegram" URL on mount so the button is
  // ready to click. Regenerated when the project changes.
  useEffect(() => {
    setInviteUrl(null);
    setInviteExpiresAt(null);
    setLinkNotConfigured(false);
    invite
      .mutateAsync(3600)
      .then((res) => {
        setInviteUrl(res.deep_link_short);
        setInviteExpiresAt(res.expires_at);
      })
      .catch((err) => {
        if (
          err instanceof ApiError &&
          err.status === 503 &&
          err.code === 'TELEGRAM_LINK_TOKEN_SECRET_UNSET'
        ) {
          setLinkNotConfigured(true);
          return;
        }
        const s = summarizeApiError(err, {
          fallbackTitle: 'Could not generate invite link',
        });
        toast.error(s.title, { description: s.description });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const botUsername = config.data?.telegram_bot_username ?? null;
  const fullDeepLink = inviteUrl ? fillBotUsername(inviteUrl, botUsername) : null;
  const botMissing = !botUsername;

  async function copyLink(): Promise<void> {
    if (!fullDeepLink) return;
    try {
      await navigator.clipboard.writeText(fullDeepLink);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy — please copy the link manually.');
    }
  }

  async function regenerateInvite(): Promise<void> {
    try {
      const res = await invite.mutateAsync(3600);
      setInviteUrl(res.deep_link_short);
      setInviteExpiresAt(res.expires_at);
      toast.success('New invite link generated');
    } catch (e) {
      if (
        e instanceof ApiError &&
        e.status === 503 &&
        e.code === 'TELEGRAM_LINK_TOKEN_SECRET_UNSET'
      ) {
        setLinkNotConfigured(true);
        return;
      }
      const s = summarizeApiError(e, {
        fallbackTitle: 'Could not generate invite link',
      });
      toast.error(s.title, { description: s.description });
    }
  }

  async function handleDisable(): Promise<void> {
    try {
      await disable.mutateAsync();
      toast.success('Auto-Campaign disabled');
    } catch (e) {
      const s = summarizeApiError(e, { fallbackTitle: 'Could not disable' });
      toast.error(s.title, { description: s.description });
    }
  }

  return (
    <Section>
      <div className="space-y-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-600 dark:text-emerald-400">
            ✓ Auto-campaign enabled
          </div>
          <h2 className="mt-1 text-xl font-semibold tracking-tight">
            Auto-Campaign for {project.name}
          </h2>
        </div>

        {linkNotConfigured ? (
          <Callout tone="warning" title="Telegram link tokens not configured">
            The <code className="font-mono text-xs">TELEGRAM_LINK_TOKEN_SECRET</code>
            {' '}env var isn't set on this deployment. Ask an admin to configure the
            Telegram bot secret before you can link chats.
          </Callout>
        ) : botMissing ? (
          <Callout tone="warning" title="Telegram bot not configured">
            The <code className="font-mono text-xs">TELEGRAM_BOT_USERNAME</code>
            {' '}env var isn't set on this deployment. Ask an admin to add it before
            you can link chats.
          </Callout>
        ) : (
          <Callout
            tone="warning"
            title="Link Telegram to receive digests"
            icon={MessageCircle}
          >
            Digests are delivered via Telegram bot only. You must link at least
            one chat before the first fire runs.
          </Callout>
        )}

        <div className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-3">
          <div className="text-sm font-medium">Link your Telegram</div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="lg"
              disabled={!fullDeepLink || linkNotConfigured || botMissing}
              asChild={Boolean(fullDeepLink) && !linkNotConfigured && !botMissing}
            >
              {fullDeepLink && !linkNotConfigured && !botMissing ? (
                <a href={fullDeepLink} target="_blank" rel="noreferrer">
                  <Send className="h-4 w-4" />
                  Link Telegram Bot
                </a>
              ) : (
                <>
                  {invite.isPending ? <Spinner className="mr-2" /> : null}
                  Link Telegram Bot
                </>
              )}
            </Button>
            {inviteExpiresAt && !linkNotConfigured ? (
              <div className="flex items-center text-xs text-muted-foreground">
                Link valid until {new Date(inviteExpiresAt).toLocaleTimeString()}
              </div>
            ) : null}
          </div>

          {fullDeepLink ? (
            <p className="break-all rounded bg-muted/50 p-2 font-mono text-xs text-muted-foreground">
              {fullDeepLink}
            </p>
          ) : null}
        </div>

        <div className="rounded-lg border border-border/60 bg-card/40 p-4 space-y-3">
          <div className="text-sm font-medium">Invite a teammate</div>
          <p className="text-sm text-muted-foreground">
            Copy this link and send it to a teammate — they open it in Telegram
            to join.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={copyLink}
              disabled={!fullDeepLink || linkNotConfigured}
            >
              <Copy className="h-3.5 w-3.5" />
              Copy invite link
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={regenerateInvite}
              disabled={invite.isPending || linkNotConfigured}
            >
              {invite.isPending ? <Spinner className="mr-2" /> : null}
              Regenerate
            </Button>
          </div>
        </div>

        <div className="flex justify-end border-t border-border/60 pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDisable}
            disabled={disable.isPending}
          >
            {disable.isPending ? <Spinner className="mr-2" /> : null}
            Disable Auto-Campaign
          </Button>
        </div>
      </div>
    </Section>
  );
}
